// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useToast } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import {
  DEFAULT_ROW_SCHEDULE,
  EmbeddedRowModal,
  LearnMoreRecommendedRowsModal,
  MenuCategoryTabs,
  MenuRowHeader,
  MenuRowList,
  MenuRowModal,
  MultiThematicRowBanner,
  StoreMenuHeader,
} from '@jane/business-admin/components';
import {
  useCreateMenuRow,
  useFetchBloomMenuByStoreId,
  useProductTypes,
  useStoreMenuMetadata,
} from '@jane/business-admin/data-access';
import { useModalActionsWithTracking } from '@jane/business-admin/hooks';
import type { EmbeddedMenuRow, MenuRow } from '@jane/business-admin/types';
import {
  EventNames,
  ModalNames,
  normalizePath,
  track,
} from '@jane/business-admin/util';
import { SDKProvider } from '@jane/shared-ecomm/providers';

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  ...spacing({ px: 64, pb: 40, pt: 24 }),
});

export const StoreMenu = () => {
  const { id: storeId = '' } = useParams<'id'>();
  const { pathname } = useLocation();
  const toast = useToast();

  const [menuRowModalOpen, setMenuRowModalOpen] = useState(false);
  const [embeddedRowModalOpen, setEmbeddedRowModalOpen] = useState(false);
  const [embeddedRow, setEmbeddedRow] = useState<
    EmbeddedMenuRow | null | undefined
  >();
  const [selectedRow, setSelectedRow] = useState<MenuRow | undefined>();
  const [selectedRowHasEmbeddedRow, setSelectedRowHasEmbeddedRow] =
    useState(false);

  const showMultipleThematicRows = useFlag(FLAGS.comm3MultipleThematicRows);
  const showEmbeddedMenuRows = useFlag(FLAGS.comm2EmbeddedMenuRows);

  const { data: allProductTypes, isFetched: productTypesLoaded } =
    useProductTypes();

  const { mutate: createMenuRow } = useCreateMenuRow(storeId);
  const { data: menuMetadata } = useStoreMenuMetadata(storeId);
  const { data: bloomMenu = null, isLoading: isLoadingBloomMenu } =
    useFetchBloomMenuByStoreId(storeId);
  const magicRowCount = menuMetadata?.magic_row_count || 0;

  const shouldShowEmbeddedMenuRows = useMemo(() => {
    return showEmbeddedMenuRows && !!bloomMenu && !isLoadingBloomMenu;
  }, [showEmbeddedMenuRows, bloomMenu, isLoadingBloomMenu]);

  const handleAddMagicRow = () => {
    createMenuRow(
      {
        enabled: true,
        row_type: 'magic_row',
        menu_row_type: 'magic_row',
        schedule: {
          ...DEFAULT_ROW_SCHEDULE,
          enabled: true,
        },
        ranking: 0,
      },
      {
        onSuccess: () => {
          toast.add({
            label: 'Recommended row added successfully',
            variant: 'success',
          });
        },
        onError: () => {
          toast.add({
            label: 'Failed to add recommended row',
            variant: 'error',
          });
        },
      }
    );
  };

  const openMenuRowModal = (row: MenuRow, hasEmbeddedRow?: boolean) => {
    track({
      event: EventNames.OpenedModal,
      modal_name: ModalNames.EditMenuRow,
      url: normalizePath(pathname, storeId),
    });

    setSelectedRow(row);
    setSelectedRowHasEmbeddedRow(hasEmbeddedRow ?? false);
    setMenuRowModalOpen(true);
  };

  const openEmbeddedRowModal = (
    menuRow: MenuRow,
    row?: EmbeddedMenuRow | null
  ) => {
    track({
      event: EventNames.OpenedModal,
      modal_name: ModalNames.EmbeddedMenuRow,
      url: normalizePath(pathname, storeId),
    });

    setEmbeddedRow(row);

    if (menuRow) {
      setSelectedRow(menuRow);
    }
    setEmbeddedRowModalOpen(true);
  };

  const {
    modalOpen: learnMoreModalOpen,
    openModal: setLearnMoreModalOpen,
    closeModal: setLearnMoreModalClose,
  } = useModalActionsWithTracking(ModalNames.LearnMoreRecommendedRows);

  return (
    <>
      <Wrapper>
        <StoreMenuHeader />
        <MenuCategoryTabs />
        <MenuRowHeader />
        {showMultipleThematicRows && menuMetadata && magicRowCount < 3 && (
          <MultiThematicRowBanner
            setLearnMoreModalOpen={setLearnMoreModalOpen}
            onAddRow={handleAddMagicRow}
            magicRowCount={magicRowCount}
          />
        )}
        <SDKProvider appMode="default">
          <MenuRowList
            onOpenEmbeddedRowModal={openEmbeddedRowModal}
            onOpenMenuRowModal={openMenuRowModal}
            showEmbeddedMenuRows={shouldShowEmbeddedMenuRows}
            storeId={storeId}
          />
        </SDKProvider>
      </Wrapper>
      {menuRowModalOpen && selectedRow && allProductTypes && (
        <MenuRowModal
          row={selectedRow}
          allProductTypes={allProductTypes}
          productTypesLoaded={productTypesLoaded}
          hasEmbeddedMenuRow={selectedRowHasEmbeddedRow}
          closeModal={() => {
            track({
              event: EventNames.ClosedModal,
              modal_name: ModalNames.EditMenuRow,
              url: normalizePath(pathname, storeId),
            });
            setMenuRowModalOpen(false);
          }}
        />
      )}
      {shouldShowEmbeddedMenuRows && embeddedRowModalOpen && (
        <EmbeddedRowModal
          bloomTheme={bloomMenu?.theme}
          isLoadingBloomTheme={isLoadingBloomMenu}
          closeModal={() => {
            track({
              event: EventNames.ClosedModal,
              modal_name: ModalNames.EmbeddedMenuRow,
              url: normalizePath(pathname, storeId),
            });
            setEmbeddedRowModalOpen(false);
          }}
          embeddedMenuRow={embeddedRow}
          menuRow={{
            custom_display_name: selectedRow?.custom_display_name,
            id: selectedRow?.id,
            menu_row_type: selectedRow?.menu_row_type ?? null,
            store_id: selectedRow?.store_id,
          }}
          menuUrl={bloomMenu?.menu_url ?? ''}
          storeId={Number(storeId)}
        />
      )}
      {showMultipleThematicRows && learnMoreModalOpen && (
        <LearnMoreRecommendedRowsModal
          closeModal={setLearnMoreModalClose}
          open={learnMoreModalOpen}
          onAddRow={handleAddMagicRow}
          magicRowCount={magicRowCount}
        />
      )}
    </>
  );
};
